import { Typography } from '@mui/material';
import {
  AutocompleteInput,
  BooleanInput,
  DateTimeInput,
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { UpdateChannel } from 'types/UpdateChannel';

interface AddSoftwareReleaseFormProps {
  record?: UpdateChannel;
  resource?: string;
}

const ReleaseInputs = () => {
  const releaseSelected = useWatch({ name: 'software_release_id' });
  const releaseNow = useWatch({ name: 'release_now' });

  return (
    releaseSelected && (
      <>
        <DateTimeInput source="released_at" fullWidth disabled={releaseNow} />
        <BooleanInput source="release_now" />
      </>
    )
  );
};

export const AddSoftwareReleaseForm = (props: AddSoftwareReleaseFormProps) => {
  const record = useRecordContext<UpdateChannel>(props);
  const translate = useTranslate();
  const { setValue, clearErrors } = useFormContext();

  /**
   * Same fallback as the hardware input's own `defaultValue`. That input seeds `hardware_id` with
   * `resetField`, and a later `<Form>` reset with non-empty defaults drops it from the emitted values;
   * the input re-registers it without a notification, so a plain watch would stay `undefined` while
   * the form holds the seeded id. Only `undefined` falls back: a cleared field is `null` and stays so.
   */
  const selectedHardwareId = useWatch({ name: 'hardware_id', defaultValue: record?.hardware_id });
  const selectedRevisionId = useWatch({ name: 'hardware_revision_id' });

  /**
   * Each level of the cascade only offers choices for the level above it, so a value picked under
   * the previous parent is stale once that parent changes: left in place, it keeps filtering the next
   * level by the old parent (TVR-2378). Cleared to `null`, not `''`, because the render gates below
   * test `!= null`.
   *
   * Driven by the inputs' own `onChange`, which react-admin calls only for a user change. Seeding
   * `hardware_id` from `record.hardware_id` goes through `useController`'s `defaultValue` and
   * `resetField`, neither of which calls it, so opening the dialog clears nothing.
   *
   * Their errors go too. A Required left by an earlier submit, or a server message about the old
   * value, would otherwise outlive the clear, and reappear if the input remounts. `setValue` does not
   * validate, so a cleared field shows nothing until the next submit checks it again.
   */
  const clearFields = (...sources: string[]) => {
    sources.forEach((source) => setValue(source, null, { shouldDirty: true }));
    clearErrors(sources);
  };

  const handleHardwareChange = () => {
    clearFields('hardware_revision_id', 'software_release_id');
  };

  const handleRevisionChange = () => {
    clearFields('software_release_id');
  };

  return (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {translate('resources.channel_releases.forms.create.header')}
      </Typography>
      <ReferenceInput reference="hardware" source="hardware_id">
        <AutocompleteInput
          optionText="name"
          label="resources.update_channels.fields.hardware_id"
          defaultValue={record?.hardware_id}
          fullWidth
          validate={required()}
          onChange={handleHardwareChange}
        />
      </ReferenceInput>
      {selectedHardwareId != null && (
        <ReferenceInput
          reference="hardware_revisions"
          source="hardware_revision_id"
          filter={{ hardware_id: selectedHardwareId }}
        >
          <SelectInput
            optionText="name"
            label="resources.update_channels.fields.hardware_revision.name"
            fullWidth
            validate={required()}
            onChange={handleRevisionChange}
          />
        </ReferenceInput>
      )}
      {selectedRevisionId != null && (
        <ReferenceInput
          reference="software_releases"
          source="software_release_id"
          filter={{ hardware_revision_id: selectedRevisionId }}
        >
          <AutocompleteInput optionText="name" fullWidth validate={required()} />
        </ReferenceInput>
      )}
      <ReleaseInputs />
      <TextInput source="update_channel_id" type="hidden" defaultValue={record?.id} sx={{ display: 'none' }} />
    </>
  );
};
